import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";
import {
	Badge,
	Box,
	ButtonProps,
	Divider,
	Flex,
	Group,
	Image,
	Indicator,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import moment from "moment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

import { ThemedButton } from "@/components/ThemedButton";

import { useAuth } from "@/hooks/selectors/useAuth";

import ModalCertificates from "@/views/pages/learner/course/ModalCertificates";

import { ReactComponent as CertificateIcon } from "@/assets/img/Course/registration/success.svg";
import { ReactComponent as BookIcon } from "@/assets/img/Course/upcoming/book.svg";
import { ReactComponent as CalenderIcon } from "@/assets/img/Course/upcoming/calendar.svg";
import { ReactComponent as DurationIcon } from "@/assets/img/Course/upcoming/duration-green.svg";
import { ReactComponent as MapMarkerIcon } from "@/assets/img/Course/upcoming/map-marker.svg";
import { ReactComponent as SupportIcon } from "@/assets/img/Course/upcoming/support.svg";
import { ReactComponent as TeachIcon } from "@/assets/img/Course/upcoming/teach.svg";
import defaultCourse from "@/assets/img/default_course.jpg";
import GreenWaves from "@/assets/img/green-wave.svg";

import { PrintCourseDate } from "../../common_function/function";
import WaveAnimation from "../WaveAnimation";

const ICON_SIZE = 20;

function UpcomingCoursesCard({ course, status }: { course: any; status: any }) {
	const [certModalChange, setCertModalChange] = useState(false);
	const MySwal = withReactContent(Swal);
	const authData = useAuth();
	const userData = authData.userData.user;

	const handlePopup = (e, displayMSG?: string) => {
		e.preventDefault();
		e.stopPropagation();
		MySwal.fire({
			title: "Unable to Proceed!",
			html: displayMSG,
			icon: "error",
		});
	};

	const handleSFCClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const data = {};
		const reschedule_to = course.reschedule_to;

		data.callback_url = process.env.REACT_APP_WEB_URL + "/course";
		data.course_id = reschedule_to.course.course_ref;
		data.course_fee = course.learner_sfc;
		data.courserun_id = reschedule_to.courserun_apicourserunid;
		data.courserun_start_date = reschedule_to.courserun_course_from;
		data.nric = userData.learnermaster_nric;
		data.email = userData.learnermaster_email;
		data.mobile = userData.learnermaster_mobile;
		data.tms_regform_id = course.registrationform_id_encrypt;
		data.tms_course_id = reschedule_to.courserun_course_id;
		data.tms_courserun_id = reschedule_to.courserun_id;
		data.tms_learner_id = course.learner_id;
		data.tms_learnermaster_id = userData.learnermaster_id;
		data.purpose = "Reschedule";

		const json_str = btoa(JSON.stringify(data));
		window.location.href = process.env.REACT_APP_API_URL + "/api/sfc_pay?encoded=" + json_str;
	};

	return (
		<Box bdrs={{ base: 20, md: 50 }} style={{ overflow: "hidden" }}>
			<Flex
				bdrs="50px"
				direction={{ base: "column", sm: "row" }}
				align={"normal"}
				justify={"stretch"}
				h="100%">
				<Box
					pos="relative"
					display={"flex"}
					flex={"0 0 auto"}
					w={{ base: "100%", sm: "50%" }}
					style={{ textAlign: "center" }}>
					<Image
						src={course.course.course_banner_image || defaultCourse}
						w="100%"
						h="100%"
						fit="cover"
					/>

					<Box pos="absolute" bottom={0} right={0} w="90%" h="auto">
						<WaveAnimation />
					</Box>
				</Box>
				<Flex
					direction={"column"}
					flex={"1 1 auto"}
					mt={{ base: 10, sm: 0 }}
					px={{ base: 20, xs: 20 }}
					pb={{ base: 40, sm: 20 }}
					bg="linear-gradient(to bottom, rgba(0, 0, 0, 0.0) 0%, #D1ECE7 100%)">
					<Box mt="xl">
						<CourserunStatusBadge course={course} />
						<Title order={4}>{course.course.course_title}</Title>
					</Box>
					{course.duration && (
						<Badge
							color="themed.8"
							leftSection={<DurationIcon width={17} height={17} color="white" />}
							size="lg"
							tt="none">
							<Text size="sm" fw={600}>
								{parseFloat(course.duration).toString()} Days
							</Text>
						</Badge>
					)}
					<SimpleGrid cols={{ base: 1, sm: 2 }} mt={10}>
						{[
							{
								icon: (
									<TeachIcon width={ICON_SIZE} height={ICON_SIZE} className="" color="#1E617B" />
								),
								label: "Trainer",
								children: course?.trainer?.trainer_name,
							},
							{
								icon: (
									<BookIcon width={ICON_SIZE} height={ICON_SIZE} className="" color="#1E617B" />
								),
								label: "Course ID",
								children: course?.course?.course_ref,
							},
							{
								icon: (
									<CalenderIcon width={ICON_SIZE} height={ICON_SIZE} className="" color="#1E617B" />
								),
								label: "Dates",
								children: (
									<>
										<p>
											{moment(course.courserun_course_from).format("DD MMM YYYY, ddd")} -{" "}
											{moment(course.courserun_course_to).format("DD MMM YYYY, ddd")}
										</p>
									</>
								),
							},
							{
								icon: <MapMarkerIcon width={ICON_SIZE} height={ICON_SIZE} color="#1E617B" />,
								label: "Location",
								children: course?.venue?.venue_address,
							},
							{
								icon: (
									<SupportIcon width={ICON_SIZE} height={ICON_SIZE} className="" color="#1E617B" />
								),
								label: "Student Services",
								children: (
									<>
										{course?.training_support?.user_name}
										<br />
										{course?.training_support?.user_mobile}
									</>
								),
							},
						].map((item, index) => (
							<Box key={index}>
								<Group gap={"xs"} align="flex-start" wrap="nowrap">
									<Box>{item.icon}</Box>
									<Stack gap={0} flex={"1 1 auto"} style={{ flexShrink: 1 }} mt={2}>
										<Text size="sm" fw={700} c={"black"}>
											{item.label}
										</Text>
										<Text size="sm" c="black">
											{item.children}
										</Text>
									</Stack>
								</Group>
							</Box>
						))}
					</SimpleGrid>
					<Divider mt={20} mb={30} />

					<Group>
						<ThemedButton
							size="xs"
							component={Link}
							to="/course/course-details/"
							state={{ data: course, status: status }}>
							View Details
						</ThemedButton>
						{course.attendance_status != "Pending Assessment" && (
							<>
								{status == "upcoming" &&
									course.reschedule_status != "Submitted" &&
									course.withdrawal_status != "PENDING" && (
										<>
											{!course.cannot_select_food_reason ? (
												<LinkButton
													variant="filled"
													to="/course/meal-selection"
													state={{
														learner_id: course.learner_id,
														course: course,
														status: status,
													}}>
													Meal Selection
												</LinkButton>
											) : (
												<ThemedButton
													size="xs"
													opacity={0.5}
													onClick={(e) => handlePopup(e, course.cannot_select_food_reason)}>
													Meal Selection
												</ThemedButton>
											)}

											{course.reschedule_status == "Pending SFC Resubmission" && (
												<ThemedButton
													size="xs"
													color="orange"
													variant="light"
													leftSection={<i className="fa fa-history" aria-hidden="true"></i>}
													rightSection={<i className="fa fa-angle-right" aria-hidden="true"></i>}
													onClick={(e) => handleSFCClick(e)}>
													Resubmit SFC{" "}
												</ThemedButton>
											)}
										</>
									)}

								{status == "completed" && course.cert_file_url && (
									<>
										<ModalCertificates
											course_title={course.course_title}
											cert_file_url={course.cert_file_url}
											showModal={certModalChange}
											setShowModal={setCertModalChange}
										/>
										<Button
											className="btn btn-outline-green cert-icon"
											onClick={(e) => {
												e.stopPropagation();
												e.preventDefault();
												setCertModalChange(true);
											}}>
											<CertificateIcon />
											<span>View Certificate</span>
											<i className="fa fa-angle-right" aria-hidden="true"></i>
										</Button>
									</>
								)}
							</>
						)}
						{(status == "upcoming" || status == "to_be_reschedule") &&
						!course.cannot_reschedule_reason ? (
							<LinkButton
								to="/course/reschedule_description"
								color="themed-black.8"
								state={{
									data: {
										learner_id: course.learner_id,
										learner_sfc: course.learner_sfc,
										courserun_id: course.courserun_id,
										course_title: course.course.course_title,
										course: course,
										status: status,
									},
								}}>
								<span>Reschedule</span>
							</LinkButton>
						) : (
							<ThemedButton
								size="xs"
								color="themed-black.8"
								opacity={0.5}
								onClick={(e) => handlePopup(e, course.cannot_reschedule_reason)}>
								<span>Reschedule</span>
							</ThemedButton>
						)}

						{(status == "upcoming" || status == "to_be_reschedule") &&
						!course.cannot_withdraw_reason ? (
							<LinkButton
								to="/course/withdrawal_description"
								color="themed-black.8"
								state={{
									data: {
										learner_id: course.learner_id,
										courserun_id: course.courserun_id,
										course_title: course.course.course_title,
										course: course,
										status: status,
									},
								}}>
								Withdrawal
							</LinkButton>
						) : (
							<ThemedButton
								color="themed-black.8"
								size="xs"
								opacity={0.5}
								onClick={(e) => handlePopup(e, course.cannot_withdraw_reason)}>
								Withdrawal
							</ThemedButton>
						)}
					</Group>
				</Flex>
			</Flex>
		</Box>
	);
}

const LinkButton = (props: { to?: string; state?: any; onClick?: () => void } & ButtonProps) => {
	const { to, state, ...rest } = props;
	return (
		<ThemedButton size="xs" variant="outline" component={Link} to={to} state={state} {...rest} />
	);
};

const CourserunStatusBadge = ({
	course,
}: {
	course: {
		reschedule_status: string;
		tobe_rescheduled: boolean;
		withdrawal_status: string;
		pending_courserun?: {
			courserun_course_from: string;
			courserun_course_to: string;
		} | null;
	};
}) => {
	const course_reschedule_status = course.reschedule_status;
	const tobe_rescheduled = course.tobe_rescheduled;
	const course_withdrawal_status = course.withdrawal_status;
	const pending_assessment_date = course.pending_courserun
		? PrintCourseDate(
				course.pending_courserun.courserun_course_from,
				course.pending_courserun.courserun_course_to,
		  )
		: null;

	if (course_withdrawal_status == "PENDING") {
		return (
			<ThemedStatusBadge
				color="red"
				leftSection={<i className="fa fa-history " aria-hidden="true"></i>}>
				{"Withdrawal Processing"}
			</ThemedStatusBadge>
		);
	}

	if (course_withdrawal_status == "REJECTED") {
		return (
			<ThemedStatusBadge
				color="red"
				variant="filled"
				leftSection={<i className="fa fa-exclamation-triangle " aria-hidden="true"></i>}>
				{"Withdrawal Rejected"}
			</ThemedStatusBadge>
		);
	}

	if (tobe_rescheduled && course_reschedule_status !== "Submitted") {
		return (
			<Indicator
				position="top-start"
				processing={true}
				color="red"
				w={"fit-content"}
				top={0}
				left={0}>
				<ThemedStatusBadge
					color="red"
					leftSection={<i className="fa fa-info-circle" aria-hidden="true"></i>}>
					{"Reschedule Required"}
				</ThemedStatusBadge>
			</Indicator>
		);
	}

	if (course_reschedule_status == "Submitted") {
		return (
			<ThemedStatusBadge
				color="blue"
				leftSection={<i className="fa fa-history" aria-hidden="true"></i>}>
				{"Reschedule Processing"}
			</ThemedStatusBadge>
		);
	}

	if (
		course_reschedule_status == "Pending SFC Cancellation" ||
		course_reschedule_status == "Pending SFC Resubmission" ||
		course_reschedule_status == "Pending SFC Approval"
	) {
		return (
			<ThemedStatusBadge
				color="blue"
				leftSection={<i className="fa fa-history" aria-hidden="true"></i>}>
				{course_reschedule_status}
			</ThemedStatusBadge>
		);
	}

	if (course_reschedule_status == "Rejected") {
		return (
			<ThemedStatusBadge
				color="red"
				variant="filled"
				leftSection={<i className="fa fa-exclamation-triangle " aria-hidden="true"></i>}>
				{"Reschedule Rejected"}
			</ThemedStatusBadge>
		);
	}

	if (course_reschedule_status == "Assessment") {
		return (
			<>
				<ThemedStatusBadge
					color="blue"
					leftSection={<i className="fa fa-info-circle" aria-hidden="true"></i>}>
					{"Pending Assessment"}
				</ThemedStatusBadge>
				{pending_assessment_date && (
					<Text size="xs" c="b" mb={5}>
						Assessment Date: {pending_assessment_date}
					</Text>
				)}
			</>
		);
	}

	return null;
};

const ThemedStatusBadge = Badge.withProps({
	ml: -8,
	size: "md",
	tt: "none",
	variant: "light",
});
export default UpcomingCoursesCard;
